// The owner's privacy choices, on their profile page. Wave C item C1.7.
//
// This is the half of finding F3 a user can actually see. The routes have existed since
// C1 part 1, but until something calls them, "you may withdraw your consent" was still
// only true of the API - and DPDP s6(4) is about the ease of doing it, which is a
// property of the screen, not the endpoint.
//
// TWO AREAS, DELIBERATELY UNEQUAL IN WEIGHT.
//
// The toggles are ordinary settings: switching off nominee disclosure stops the
// dead-man's switch and changes nothing else, so it should feel no heavier than any other
// preference. That IS the compliance point - if stopping the disclosure felt as drastic
// as closing the account, we would be back to F3.
//
// Closing the account is the opposite and is separated from them by a rule, its own
// heading and red styling, because almost nobody who wants to stop the switch wants to
// end their account. Offering the two as peer checkboxes would let someone destroy their
// vault while meaning to turn off one feature.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Download, ShieldCheck, Trash2 } from 'lucide-react';
import Button from './elements/Button';
import Input from './elements/Input';
import Modal from './Modal';
import { toDate } from '../utils/globalUtils';
import { setAnalyticsPreference } from '../utils/cookieConsent';
import {
  getConsents, withdrawConsent, grantConsent, closeAccount, restoreAccount,
} from '../utils/consentApi';

// The server is the authority on which purposes exist and which may be withdrawn - it
// returns `withdrawable` derived from its own registry. This map only supplies the human
// wording, and a purpose the server sends that is missing here still renders (falling
// back to its key) rather than silently disappearing from the list.
const COPY = {
  nominee_disclosure: {
    label: 'Nominee access if you go inactive',
    on: 'If you stop logging in for 20 days, your nominees are notified and given read access to your locker.',
    off: 'Turned off. Your nominees will not be given access, and we will stop sending you activity-check reminders. Your locker, your records and your nominee list are all untouched.',
  },
  // The cookie banner splits this into two boxes (usage analytics, device diagnostics);
  // the consent model has one `analytics` purpose, because the question it has to answer
  // is s11(1)(c)'s "was anything shared with Google LLC", and either box does that. The
  // cost is that turning it back on HERE turns both categories on, so the copy sends
  // anyone who wants the finer choice to Cookie Preferences rather than pretending this
  // switch is granular. Before C1.10 this row could only ever read "not asked", since the
  // choice never left the browser (finding F16).
  //
  // The `off` line used to end "and nothing is transferred outside India", which was the
  // same false claim the Privacy Policy carried (action item A11): transactional email
  // goes through Google Workspace, so something is transferred whatever this switch says.
  // A panel whose whole purpose is to tell someone what happens to their data is the last
  // place that should overstate the effect of its own toggle.
  analytics: {
    label: 'Usage analytics',
    on: 'Google Analytics 4 measures how the site is used, which sends usage data outside India. To choose between usage analytics and device diagnostics separately, use Cookie Preferences.',
    off: 'Turned off. No analytics are loaded and no usage data goes to Google Analytics. Our transactional email is still delivered outside India, which this switch cannot change — see Privacy Policy section 5.',
  },
  referral_payouts: {
    label: 'Referral payouts',
    on: 'We use your UPI ID to pay referral rewards.',
    off: 'Turned off. Your UPI ID will not be used for payouts.',
  },
};

const ConsentSettings = ({ onNotify, onRequestExport }) => {
  const navigate = useNavigate();
  const [consents, setConsents] = useState([]);
  const [withdrawable, setWithdrawable] = useState([]);
  const [pendingErasureAt, setPendingErasureAt] = useState(null);
  const [gracePeriodDays, setGracePeriodDays] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPurpose, setBusyPurpose] = useState(null);

  const [closeOpen, setCloseOpen] = useState(false);
  const [closePassword, setClosePassword] = useState('');
  const [closeAcknowledged, setCloseAcknowledged] = useState(false);
  const [closeError, setCloseError] = useState(null);
  const [closing, setClosing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getConsents();
      if (res.success) {
        setConsents(res.consents || []);
        setWithdrawable(res.withdrawable || []);
        setPendingErasureAt(res.pendingErasureAt || null);
        setGracePeriodDays(res.gracePeriodDays || null);
      } else {
        onNotify({ message: res.message || 'Could not load your privacy choices.', type: 'error' });
      }
    } catch {
      onNotify({ message: 'Could not load your privacy choices.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [onNotify]);

  useEffect(() => { load(); }, [load]);

  const stateOf = (purpose) => consents.find((c) => c.purpose === purpose);

  const toggle = async (purpose, turnOn) => {
    setBusyPurpose(purpose);
    try {
      const res = turnOn ? await grantConsent(purpose) : await withdrawConsent(purpose);
      if (res.success) {
        // ANALYTICS ALSO HAS TO STOP IN THIS BROWSER (C1.10). GA4 loads from
        // localStorage, not from the server, so writing the row alone would leave this
        // screen saying "no analytics are loaded and nothing is transferred outside
        // India" while the script kept running in the very tab making that claim. App.js
        // subscribes to this store and re-renders, so the change lands immediately rather
        // than at the next reload.
        if (purpose === 'analytics') {
          setAnalyticsPreference(turnOn);
        }

        onNotify({ message: turnOn ? 'Turned on.' : 'Turned off. This takes effect immediately.', type: 'success' });
        await load();
      } else {
        onNotify({ message: res.message || 'Could not save that change.', type: 'error' });
      }
    } catch {
      onNotify({ message: 'Could not save that change.', type: 'error' });
    } finally {
      setBusyPurpose(null);
    }
  };

  const handleClose = async () => {
    setCloseError(null);
    if (!closePassword) return setCloseError('Your password is required.');
    if (!closeAcknowledged) return setCloseError('Please confirm you understand what happens.');

    setClosing(true);
    try {
      const res = await closeAccount(closePassword, closeAcknowledged);
      if (res.success) {
        // The server bumps sessionEpoch, so this session is ALREADY DEAD by the time the
        // response arrives - "signed out everywhere" includes the tab that asked. So this
        // must not reload the panel: that call would 401, and the pending-closure banner
        // would render for nobody. Clear local session state and send them to the front
        // door, where signing back in shows the banner and the Restore button.
        setCloseOpen(false);
        sessionStorage.clear();
        navigate('/', {
            state: { message: `${res.message} You can sign back in before ${toDate(res.erasureAt)} to restore your account.` },
        });
        return;
      }

      setCloseError(res.message || 'Could not close your account.');
    } catch {
      setCloseError('Could not close your account.');
    } finally {
      setClosing(false);
    }
  };

  const handleRestore = async () => {
    try {
      const res = await restoreAccount();
      onNotify({
        message: res.success ? 'Your account has been restored.' : (res.message || 'Could not restore your account.'),
        type: res.success ? 'success' : 'error',
      });
      if (res.success) await load();
    } catch {
      onNotify({ message: 'Could not restore your account.', type: 'error' });
    }
  };

  if (loading) return null;

  return (
    <div className="bg-primary-light p-6 rounded-xl border border-accent-main">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-6 h-6 text-accent-main" />
        <h2 className="text-2xl font-bold text-primary-dark">Privacy Choices</h2>
      </div>

      {/* A pending closure is the loudest thing on the page while it lasts: it has a
          deadline, and the only way to stop it is a button the user has to find. */}
      {pendingErasureAt && (
        <div className="mb-6 p-4 rounded border border-red-600 bg-red-50 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700 font-semibold">
              Your account is scheduled for erasure on {toDate(pendingErasureAt)}.
            </p>
            <p className="text-sm text-red-700 mt-1">
              Processing has stopped and your locker is locked: your records, files and nominees
              can&apos;t be opened or changed. They are kept until that date only so you can restore
              your account or download a copy — after it, they and your account are permanently
              deleted and cannot be recovered.
            </p>
            <div className="flex gap-2 mt-3">
              <Button type="primary" size="small" onClick={handleRestore}>Restore my account</Button>
              {onRequestExport && (
                <Button type="secondary_outline" size="small" onClick={onRequestExport}>
                  <Download className="w-4 h-4" /> Download my data
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="text-sm text-primary-dark mb-4">
        Turn off anything you no longer want. These take effect immediately and none of them delete
        your records.
      </p>

      <div className="space-y-4">
        {withdrawable.map((purpose) => {
          const consent = stateOf(purpose);
          const copy = COPY[purpose] || { label: purpose, on: '', off: '' };
          // Three states, not two: a purpose nobody has asked about yet is neither on nor
          // off, and showing it as "off" would claim the user refused something they were
          // never offered.
          const isOn = consent?.state === 'granted';
          const notAsked = !consent || consent.state === 'not_asked';

          return (
            <div key={purpose} className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 border-b border-accent-main/30 pb-4 last:border-0">
              <div className="flex-1">
                <p className="font-semibold text-primary-dark">{copy.label}</p>
                <p className="text-sm text-primary-dark mt-1">{isOn ? copy.on : copy.off}</p>
                {isOn && consent?.grantedAt && (
                  <p className="text-xs text-primary-dark/70 mt-1">
                    Agreed {toDate(consent.grantedAt)}{consent.version ? ` (notice version ${consent.version})` : ''}
                  </p>
                )}
                {consent?.state === 'withdrawn' && consent.withdrawnAt && (
                  <p className="text-xs text-primary-dark/70 mt-1">Withdrawn {toDate(consent.withdrawnAt)}</p>
                )}
                {notAsked && (
                  <p className="text-xs text-primary-dark/70 mt-1">You have not been asked about this yet.</p>
                )}
              </div>
              {/* The visible label stays short, but the ACCESSIBLE name carries the
                  purpose. Three buttons all called "Turn off" is ambiguous to anyone
                  navigating by screen reader - and it is what made the first E2E run
                  unable to tell them apart either. */}
              <Button
                type={isOn ? 'danger_outline' : 'primary_outline'}
                size="small"
                aria-label={`${isOn ? 'Turn off' : 'Turn on'} ${copy.label}`}
                loading={busyPurpose === purpose}
                disabled={busyPurpose === purpose}
                onClick={() => toggle(purpose, !isOn)}
              >
                {isOn ? 'Turn off' : 'Turn on'}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Below the rule: ending the service. Deliberately not a fourth toggle. */}
      {!pendingErasureAt && (
        <div className="mt-8 pt-6 border-t border-red-300">
          <h3 className="text-lg font-bold text-red-700 mb-1">Close my account</h3>
          <p className="text-sm text-primary-dark mb-3">
            This withdraws your consent to our privacy notice altogether, which means we can no longer
            provide the service. Everything stops straight away and your data is erased
            {gracePeriodDays ? ` ${gracePeriodDays} days later` : ' after a grace period'} — you can
            change your mind at any point before then.
          </p>
          <Button type="danger_outline" size="small" onClick={() => setCloseOpen(true)}>
            <Trash2 className="w-4 h-4" /> Close my account
          </Button>
        </div>
      )}

      <Modal
        open={closeOpen}
        onClose={() => { setCloseOpen(false); setClosePassword(''); setCloseAcknowledged(false); setCloseError(null); }}
        title="Close your account"
        icon={<AlertTriangle />}
      >
        <div className="space-y-4">
          {/* C1.4: the consequences, stated BEFORE the confirm and not behind a link.
              s6(6) lets us stop providing a service a withdrawn consent underpinned, but
              only if the user knows which service they are losing first. */}
          <div className="text-sm text-primary-dark bg-background-light p-3 rounded border border-red-400 space-y-2">
            <p className="font-semibold text-red-700">What happens when you confirm:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>You are signed out everywhere, immediately.</li>
              <li>Your locker is locked: you can no longer open or change your records, files or nominees.</li>
              <li>The dead-man&apos;s switch stops. Your nominees will not be given access.</li>
              <li>Alert emails and all other processing stop.</li>
              <li>
                Your records, files and account are permanently erased
                {gracePeriodDays ? ` ${gracePeriodDays} days from now` : ' after a grace period'}.
                Until then you can sign back in to restore your account or download your data.
              </li>
            </ul>
          </div>

          {onRequestExport && (
            <div className="flex items-center justify-between gap-3 p-3 rounded border border-accent-main">
              <p className="text-sm text-primary-dark">
                Take a copy of your data first — after the erasure date it cannot be recovered.
              </p>
              <Button type="secondary_outline" size="small" onClick={onRequestExport}>
                <Download className="w-4 h-4" /> Download
              </Button>
            </div>
          )}

          <label className="flex items-start gap-2 text-sm text-primary-dark">
            <input
              type="checkbox"
              name="closeAcknowledged"
              aria-label="I understand my data will be erased"
              className="mt-1"
              checked={closeAcknowledged}
              onChange={(e) => { setCloseAcknowledged(e.target.checked); setCloseError(null); }}
            />
            <span>I understand my data will be erased and this cannot be undone after the date above.</span>
          </label>

          <Input
            label="Password"
            type="password"
            value={closePassword}
            onChange={(e) => { setClosePassword(e.target.value); setCloseError(null); }}
            error={closeError}
            placeholder="Enter your password"
          />

          <div className="flex justify-end gap-2 mt-6">
            <Button
              type="secondary"
              onClick={() => { setCloseOpen(false); setClosePassword(''); setCloseAcknowledged(false); setCloseError(null); }}
            >
              Cancel
            </Button>
            {/* Deliberately NOT "Close my account" again: the button that opened this
                modal is still in the page behind it, and two controls with one name is
                ambiguous to a reader and to a test. "Confirm" also says which of the two
                is the irreversible one. */}
            <Button type="danger" onClick={handleClose} loading={closing} disabled={closing}>
              Confirm account closure
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ConsentSettings;
